import { ArrowRight, Pin } from "lucide-react"
import { Link } from "react-router"

import { authorNames, getAuthors } from "~/data/authors"
import { cn } from "~/lib/utils"
import type { Post } from "~/data/posts"
import { ForestBackdrop } from "~/components/landing/ForestBackdrops"

// Blog cards — index grid, featured row and the "keep reading" foot of a
// post all share this one treatment: image, title, a byline (avatar, author
// name(s), publish date). No card borders; spacing and the image do the
// separating. A post with no `cardImage` gets a branded fallback tile so the
// grid keeps its rhythm until every post has real art.

// Card art ships as a 1600px master plus an 800px sibling (see the
// `cardImage` note in data/posts.ts). Cards render at ~520px at most, so the
// 800 covers 1x screens and the master covers 2x.
function cardSrcSet(src: string) {
  return `${src.replace(/\.webp$/, "-800.webp")} 800w, ${src} 1600w`
}
const CARD_SIZES =
  "(min-width: 1640px) 520px, (min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"

function PostThumb({
  post,
  pinned,
  eager,
  className,
}: {
  post: Post
  pinned?: boolean
  /** Above the fold (the featured row): fetch immediately instead of lazily. */
  eager?: boolean
  className?: string
}) {
  return (
    <div
      className={cn(
        "relative aspect-[16/10] overflow-hidden rounded-xl bg-forest",
        className,
      )}
    >
      {post.cardImage ? (
        <img
          src={post.cardImage}
          srcSet={cardSrcSet(post.cardImage)}
          sizes={CARD_SIZES}
          width={1600}
          height={1000}
          alt=""
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
        />
      ) : (
        <>
          <img
            src="/art/topo-grid-dark-800.webp"
            alt=""
            aria-hidden
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover opacity-70 transition-all duration-300 group-hover:scale-[1.03] group-hover:opacity-90"
          />
          <img
            src="/brand/logo-symbol-lime.svg"
            alt=""
            aria-hidden
            className="absolute left-1/2 top-1/2 h-8 w-8 -translate-x-1/2 -translate-y-1/2 opacity-90 transition-transform duration-300 group-hover:scale-110"
          />
        </>
      )}
      {post.cardImageHotspot ? (
        <div
          aria-hidden
          className="pointer-events-none absolute h-16 w-16 -translate-x-1/2 -translate-y-1/2 rounded-full bg-lime opacity-0 mix-blend-screen blur-xl transition-opacity duration-300 group-hover:opacity-80"
          style={{
            top: post.cardImageHotspot.top,
            left: post.cardImageHotspot.left,
          }}
        />
      ) : null}
      {post.cardImageHalo ? (
        // Shares the image's hover zoom so the halo stays on the outline.
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 transition-transform duration-300 group-hover:scale-[1.03]"
        >
          <div
            className="absolute opacity-0 shadow-[0_0_28px_4px_theme(colors.lime)] transition-opacity duration-300 group-hover:opacity-30"
            style={{
              top: post.cardImageHalo.top,
              left: post.cardImageHalo.left,
              width: post.cardImageHalo.width,
              height: post.cardImageHalo.height,
              borderRadius: post.cardImageHalo.radius,
            }}
          />
        </div>
      ) : null}
      {post.cardImageHotspots
        ? post.cardImageHotspots.map((spot, i) => (
            <div
              key={i}
              aria-hidden
              className="pointer-events-none absolute h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full bg-lime opacity-0 mix-blend-screen blur-md motion-safe:group-hover:[animation:pin-chase_800ms_ease-in-out]"
              style={{
                top: spot.top,
                left: spot.left,
                animationDelay: `${i * 500}ms`,
              }}
            />
          ))
        : null}
      {post.cardChartLine ? (
        <div
          aria-hidden
          className="pointer-events-none absolute overflow-hidden transition-transform duration-300 group-hover:scale-[1.03]"
          style={post.cardChartLine.box}
        >
          {/* Hides the line baked into cardImage so it reads as freshly
              drawn — a flat gradient rather than the real (dashed-gridline)
              background, but close enough at card size. */}
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(to bottom, ${post.cardChartLine.maskFrom}, ${post.cardChartLine.maskTo})`,
            }}
          />
          <svg
            viewBox={`0 0 ${post.cardChartLine.viewBox}`}
            preserveAspectRatio="none"
            className="absolute inset-0 h-full w-full"
          >
            <path
              d={post.cardChartLine.path}
              fill="none"
              stroke={post.cardChartLine.color}
              strokeWidth={3.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              pathLength={1}
              className="opacity-0 [stroke-dasharray:1] [stroke-dashoffset:1] [transition:opacity_150ms_ease-out,stroke-dashoffset_2600ms_linear] group-hover:opacity-100 group-hover:[stroke-dashoffset:0]"
            />
          </svg>
        </div>
      ) : null}
      {post.status === "placeholder" ? (
        <span className="absolute left-3 top-3 rounded-full bg-forest/80 px-2.5 py-0.5 font-flex font-medium text-[10px] uppercase tracking-wide text-lime">
          Coming soon
        </span>
      ) : null}
      {pinned ? (
        <span
          className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-forest/80 text-lime"
          title="Pinned post"
        >
          <Pin className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
        </span>
      ) : null}
    </div>
  )
}

// Author avatar(s) + name(s), plus the publish date — the credit a card
// needs; the full author list (and interviewees) live on the post itself.
// One author: a single avatar + full name. More than one (e.g. the founders'
// joint launch post): a small overlapping avatar stack + "A and N others".
function AuthorAvatar({
  author,
  className,
}: {
  author: { avatar?: string; initials: string; name: string }
  className?: string
}) {
  return author.avatar ? (
    <img
      src={author.avatar}
      alt=""
      className={cn("shrink-0 rounded-full object-cover", className)}
    />
  ) : (
    <span
      aria-hidden
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full bg-forest font-serif italic text-lime",
        className,
      )}
    >
      {author.initials}
    </span>
  )
}

function CardByline({ post }: { post: Post }) {
  const authors = getAuthors(post.authors)
  if (authors.length === 0) return null

  return (
    <div className="flex items-center gap-2">
      {authors.length > 1 ? (
        <span className="flex shrink-0 -space-x-2">
          {authors.map((author) => (
            <AuthorAvatar
              key={author.name}
              author={author}
              className="h-6 w-6 text-[9px] ring-2 ring-paper"
            />
          ))}
        </span>
      ) : (
        <AuthorAvatar author={authors[0]} className="h-6 w-6 text-[9px]" />
      )}
      <p className="text-[13px] text-mute">
        <span className="font-medium text-ink">{authorNames(authors)}</span>
        {post.publishedDisplay ? ` · ${post.publishedDisplay}` : null}
      </p>
    </div>
  )
}

// Dark-tone sibling of CardByline, for a card sitting on a forest/image
// backdrop (BannerPostCard) rather than a light one — same avatar + name
// treatment, just paper text and a forest avatar ring instead of ink/paper.
function BannerByline({ post }: { post: Post }) {
  const authors = getAuthors(post.authors)
  if (authors.length === 0) return null

  return (
    <span className="flex shrink-0 items-center gap-1.5">
      <span className="flex shrink-0 -space-x-1.5">
        {authors.map((author) => (
          <AuthorAvatar
            key={author.name}
            author={author}
            className="h-5 w-5 text-[9px] ring-2 ring-forest"
          />
        ))}
      </span>
      <span>{authorNames(authors)}</span>
    </span>
  )
}

// The large cards at the top of the index — same anatomy as FeedPostCard,
// just bigger, and pinned (a small badge on the thumb) since these are the
// handful of posts editorially chosen to lead.
export function FeaturedPostCard({ post }: { post: Post }) {
  return (
    <Link to={`/blog/${post.slug}`} className="group flex flex-col">
      <PostThumb post={post} pinned eager />
      <div className="mt-5 flex flex-col gap-3">
        <h3 className="font-sans text-[21px] font-medium leading-snug tracking-tight text-ink transition-colors group-hover:text-forest">
          {post.title}
        </h3>
        <p className="text-[15px] leading-relaxed text-mute">{post.summary}</p>
        <CardByline post={post} />
      </div>
    </Link>
  )
}

export function FeedPostCard({
  post,
  eager,
}: {
  post: Post
  /** See PostThumb — the index passes this for its first row. */
  eager?: boolean
}) {
  return (
    <Link to={`/blog/${post.slug}`} className="group flex flex-col">
      <PostThumb post={post} eager={eager} />
      <div className="mt-4 flex flex-col gap-2">
        <h3 className="font-sans text-[18px] font-medium leading-snug tracking-tight text-ink transition-colors group-hover:text-forest">
          {post.title}
        </h3>
        <CardByline post={post} />
      </div>
    </Link>
  )
}

// Quiet one-line sibling of BannerPostCard, for phones. The full dark banner
// is the loudest thing on a small screen and reads as a section break, so
// below sm a section's related post is just a title, a byline and an arrow.
export function CompactPostRow({ post }: { post: Post }) {
  const authors = getAuthors(post.authors)
  const meta = [
    authors.length > 0 ? authorNames(authors) : null,
    post.status === "placeholder" ? "Coming soon" : post.publishedDisplay,
  ]
    .filter(Boolean)
    .join(" · ")

  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group flex items-center justify-between gap-4 rounded-2xl border border-line px-5 py-4 transition-colors hover:border-chartreuse-deep"
    >
      <span className="flex min-w-0 flex-col gap-1">
        <span className="font-sans text-[15px] font-medium leading-snug text-forest">
          {post.title}
        </span>
        <span className="text-[13px] text-mute">{meta}</span>
      </span>
      <ArrowRight
        className="h-4 w-4 shrink-0 text-chartreuse-deep transition-transform group-hover:translate-x-0.5"
        strokeWidth={2}
        aria-hidden
      />
    </Link>
  )
}

// Long forest banner variant. Anna's swap: use the real design-team cover
// art (Figma "Cover - Catchment", ~5.6:1, the source the card thumbnail is
// itself cropped from) as the banner's own full-bleed background — the
// image already bleeds into the dark texture with no baked text, built for
// exactly this shape, instead of the constructed ForestBackdrop-plus-boxed-
// thumbnail approximation. Falls back to that construction for posts that
// don't have a `bannerImage` exported at this aspect yet.
export function BannerPostCard({ post }: { post: Post }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group relative flex min-h-[220px] flex-col gap-6 overflow-hidden rounded-2xl bg-forest px-6 py-8 sm:flex-row sm:items-center sm:px-10"
    >
      {post.bannerImage ? (
        <img
          src={post.bannerImage}
          alt=""
          aria-hidden
          loading="lazy"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
        />
      ) : (
        <ForestBackdrop lazy />
      )}
      <div className="relative flex flex-col gap-3 sm:max-w-lg">
        {post.status === "placeholder" ? (
          <span className="w-fit rounded-full bg-forest/80 px-2.5 py-0.5 font-flex font-medium text-[10px] uppercase tracking-wide text-lime ring-1 ring-lime/30">
            Coming soon
          </span>
        ) : null}
        <h3 className="font-sans text-2xl font-semibold leading-tight tracking-tight text-paper sm:text-[28px]">
          {post.title}
        </h3>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-paper/60">
          <BannerByline post={post} />
          {/* same rule as CompactPostRow: a placeholder has no date yet */}
          {post.status === "placeholder" || !post.publishedDisplay ? null : (
            <>
              {post.authors && post.authors.length > 0 ? (
                <span className="text-paper/30" aria-hidden>
                  ·
                </span>
              ) : null}
              <span>{post.publishedDisplay}</span>
            </>
          )}
          <ArrowRight
            className="h-3.5 w-3.5 -translate-x-1 text-lime opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100"
            strokeWidth={2}
            aria-hidden
          />
        </div>
      </div>
      {!post.bannerImage && post.cardImage ? (
        <div className="relative ml-auto aspect-[16/10] w-full overflow-hidden rounded-xl ring-1 ring-lime/40 sm:w-72 sm:shrink-0">
          <img
            src={post.cardImage}
            alt=""
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />
        </div>
      ) : null}
    </Link>
  )
}
